import { FC, useEffect, useState } from 'react'
import { Formik, Form, FieldArray, ErrorMessage } from 'formik'
import { HobbiesAutocompleteField } from './fields/HobbiesAutocompleteField'
import CityAutocompleteField from '../../../modules/auth/components/fields/CityAutocompleteField'
import ClubesAutocompleteField from './fields/ClubesAutocompleteField'
import { updateProfilePersonal } from '../../../services/profileAdminService'
import { useHistory } from 'react-router-dom'
import { useUserProfileContext } from '../../../context/UserProfileContext'
import { useAlert } from '../../../hooks/utils/useAlert'
import * as Yup from 'yup'
import { KTSVG } from '../../../../_metronic/helpers'
import { RequiredInfoModal } from '../../../helpers/RequiredInfoModal'
import { showErrorAlert } from '../../../helpers/alert'
import { getPersonalInterests } from '../../../services/componentsService'

const ProfileAdminPersonalForm: FC = () => {
  const [selectedHobbies, setSelectedHobbies] = useState<string[]>([])
  const [selectedInterests, setSelectedInterests] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const navigate = useHistory()
  const { refetch, searchParams, data } = useUserProfileContext()
  const { showError } = useAlert()
  const [showModal, setShowModal] = useState(false);
  const [messageModal, setMessageModal] = useState("");
  const [allInterests, setAllInterests] = useState([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    getInterest()
  }, [])
  
  useEffect(() => {
    if (data?.personalDetail?.hobbies && data.personalDetail.hobbies.length > 0) {
      setSelectedHobbies(data.personalDetail.hobbies)
    }
  }, [data])

  const getInterest = async () => {
    const data_ = await getPersonalInterests();
    const result = (data_ ?? []).map((item) => item.name);
    let selectedInterses_ = (data?.personalDetail?.interests ?? []).filter(interest =>
      result.some(ai => ai.trim().toLowerCase() === interest.trim().toLowerCase())
    )
    console.log('selectedInterses_', selectedInterses_)
    setSelectedInterests(selectedInterses_)
    setAllInterests(result);
    setReady(true);
  }

  const initialValues = {
    hobbies: data?.personalDetail?.hobbies ?? [],
    clubs: data?.personalDetail?.clubs ?? [],
    interests: selectedInterests
  };

  const validationSchema = Yup.object({
    hobbies: Yup.array().of(
      Yup.string().max(100, "Max 100 characters"),
    ).nullable(),
    interests: Yup.array().of(
      Yup.string().max(100, "Max 100 characters"),
    ).nullable(),
    clubs: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().trim().max(220, "Max 220 characters"),
        city: Yup.string().trim().max(220, "Max 220 characters"),
      })
    ).nullable(),
  })

  if(!ready) return null;

  return (
    <div className='pf-personal'>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { resetForm }) => {
          try {
            setLoading(true)

            const payload = {
              ...values,
            }
            let id = !!searchParams.userSelected ? searchParams.userSelected : '';
            let isUpdate = await updateProfilePersonal(payload,id);
            if(isUpdate?.success || isUpdate?.email || isUpdate){
              showErrorAlert({title:'Saved',message:'Your information has been saved successfully.'});
              await refetch()
              navigate.goBack()
            }
          } catch (error: any) {
            const statusCode = error?.response?.status || 500
            showError({
              title: 'Unable to update personal details',
              message:
                "We couldn't save your changes. Please review your information and try again.",
              errorCode: `PERSONAL_DETAILS_${statusCode}`,
            })
          } finally {
            setLoading(false)
          }
        }}
      >
        {(formik) => {
          const handleAddHobbies = (values:string[]) => {
            formik.setFieldValue("hobbies", values);
            formik.setFieldTouched('hobbies', true, false)
          };

          const focusFirstError = async () => {
            const errors = await formik.validateForm();
            const firstErrorPath = findFirstErrorPath(errors);

            if (firstErrorPath) {
              formik.setTouched(touchAll(formik.values))
              setMessageModal(`Please check the field error '${firstErrorPath.replaceAll('_', ' ')}' to save your progress.`)
              const el = document.querySelector(`[name="${firstErrorPath}"]`) as HTMLElement
              setShowModal(true)
              el?.focus()
              return
            }

            formik.submitForm()
          }

          const touchAll = (values: any): any => {
            if (Array.isArray(values)) {
              return values.map((v) => touchAll(v))
            }
            if (typeof values === 'object' && values !== null) {
              return Object.keys(values).reduce((acc, key) => {
                acc[key] = touchAll(values[key])
                return acc
              }, {} as any)
            }
            return true
          }

          const findFirstErrorPath = (errors: any, path = ''): string | null => {
            if (typeof errors === 'string') {
              return path
            }

            if (Array.isArray(errors)) {
              for (let i = 0; i < errors.length; i++) {
                const res = findFirstErrorPath(
                  errors[i],
                  `#${i+1}`
                )
                if (res) return res
              }
            }

            if (typeof errors === 'object' && errors !== null) {
              for (const key of Object.keys(errors)) {
                const res = findFirstErrorPath(
                  errors[key],
                  path ? `${key} ${path}` : key
                )
                if (res) return res
              }
            }

            return null
          }

          const toggleInterest = (interest: string) => {
            const interests = formik.values.interests || []
            if (interests.includes(interest)) {
              // remover
              formik.setFieldValue(
                'interests',
                interests.filter((i) => i !== interest)
              )
            } else {
              // agregar
              formik.setFieldValue(
                'interests',
                [...interests, interest]
              )
            }
          }

          return (
          <Form className='pf-personal__form'>
            {/* Header */}
            <div className='pf-personal__header'>Edit Your Personal Details</div>

            <div className='pf-personal__sections'>
              {/* Hobbies */}
              <div className='pf-personal__section'>
                <div className='pf-personal__title'>Your Hobbies</div>
                <div className='pf-personal__field'>
                  <label className='pf-personal__label'>List your hobbies</label>
                  <HobbiesAutocompleteField values={formik.values.hobbies} onChange={handleAddHobbies}/>
                  <div className='pf-personal__desc'>Type and press Enter</div>
                  <ErrorMessage name="hobbies" component="div" className="tap-add-experience-text text-danger" />
                </div>
              </div>

              {/* Clubs */}
              <div className='pf-personal__section'>
                <div className='pf-personal__title'>Your Clubs</div>
                <div className='pf-personal__desc'>
                  List any clubs or associations you belong to
                </div>
                <FieldArray name="clubs">
                  {({ push, remove }) => (<>
                    <div className='d-flex flex-column w-100'>
                      {formik.values.clubs.map((item, index) => (
                        <div key={`clubs-${index}`} className='d-flex flex-column'>
                          <div className="w-100 d-flex flex-row gap-4" style={{ marginBottom: "24px" }}>
                            {/* START FIELDS */}
                            <div className="tap-add-experience-2-enhancement-input">
                              <label className="tap-add-experience-2-label">Club Name</label>
                              <div className="tap-add-experience-2-input-line">
                                <input value={item.name} onChange={(value) => formik.setFieldValue(`clubs[${index}].name`, value.target.value)} className="tap-add-experience-2-input" />
                              </div>
                            </div>
                            <div className="tap-add-experience-2-enhancement-input">
                              <label className="tap-add-experience-2-label">City</label>
                              <div className="tap-add-experience-2-input-line">
                                {/* <input value={item.city} onChange={(value) => formik.setFieldValue(`clubs[${index}].city`, value.target.value)} className="tap-add-experience-2-input" /> */}
                                <CityAutocompleteField name={`clubs[${index}].city`} />
                              </div>
                            </div>
                            {/* REMOVE BUTTON */}
                            {index >= 0 && (<>
                              <button type="button" onClick={() => remove(index)} className="remove-btn tap-flex-center">
                                <KTSVG path='/media/svg/nobilis/teams_and_partner/team_and_partner_experiences_add_experience_availability_date_delete.svg' />&nbsp;
                              </button>
                            </>)}
                          </div>
                          <div className='w-100 d-flex flex-row'>
                            <ErrorMessage name={`clubs[${index}].name`} component="div" className="tap-add-experience-text text-danger w-50" />
                            <ErrorMessage name={`clubs[${index}].city`} component="div" className="tap-add-experience-text text-danger w-50" />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div role="button" onClick={() => push({ name: undefined, city: undefined })} className="tap-add-experience-2-add-text mb-5">
                      + Add more
                    </div>
                  </>)}
                </FieldArray>
              </div>

              {/* Personal Interests */}
              <div className='pf-personal__section'>
                <div className='pf-personal__title'>
                  Please indicate your key personal interests
                </div>
                <div className='pf-personal__desc'>Select all that apply</div>

                <div className='pf-personal__tags'>
                  {allInterests.map((interest) => (
                    <div key={interest}
                      onClick={() => toggleInterest(interest)}
                      className={`pf-personal__tag ${formik.values.interests.includes(interest) ? 'pf-personal__tag--selected' : ''
                        }`}>
                      {interest}
                    </div>
                  ))}
                </div>
                <ErrorMessage name={`interests`} component="div" className="tap-add-experience-text text-danger" />
              </div>
            </div>

            {/* Actions */}
            <div className='pf-personal__actions'>
              <button type='button' className='pf-personal__btn pf-personal__btn--secondary' onClick={() => navigate.goBack()}>
                Cancel
              </button>

              <button
                type='button'
                className='btn nb-btn-primary'
                disabled={loading}
                aria-busy={loading ? 'true' : 'false'}
                aria-live='polite'
                onClick={focusFirstError}
              >
                {!loading ? (
                  <>
                    <span className='nb-heading-md'>save changes</span>
                    <img
                      src='/media/svg/nobilis/vector1.svg'
                      alt=''
                      className='nb-btn-icon nb-btn-icon--white'
                    />
                  </>
                ) : (
                  <span className='indicator-progress nb-heading-md'>
                    Please wait...
                    <span
                      className='spinner-border spinner-border-sm align-middle ms-2'
                      role='status'
                      aria-hidden='true'
                    />
                  </span>
                )}
              </button>
            </div>
          </Form>)
        }}
      </Formik>
      <RequiredInfoModal open={showModal} message={messageModal} onClose={() => setShowModal(false)} />
    </div>
  )
}

export default ProfileAdminPersonalForm
